import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "../../utils/axios";
import { base_url } from "../../utils/axiosConfig";
import HD from "./Hederdisp";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  @keyframes hm2FadeIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Nav Bar ── */
  .hm2-bar {
    background: #ffffff;
    border-bottom: 1.5px solid #f0f0f0;
    position: relative;
    z-index: 990;
    font-family: 'Inter', sans-serif;
  }

  .hm2-inner {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    align-items: stretch;
    gap: 0;
    height: 44px;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .hm2-inner::-webkit-scrollbar { display: none; }

  /* ── "Categories ▾" bordered button ── */
  .hm2-cat-wrap {
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-right: 6px;
  }

  .hm2-cat-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 14px;
    height: 30px;
    background: transparent;
    color: #1e293b;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 700;
    border-radius: 6px;
    border: 1.5px solid #1e293b;
    cursor: pointer;
    white-space: nowrap;
    transition: border-color 0.18s, color 0.18s, background 0.18s;
    user-select: none;
  }
  .hm2-cat-btn:hover,
  .hm2-cat-btn.open {
    border-color: #14b8a6;
    color: #14b8a6;
    background: #f0fdfa;
  }
  .hm2-cat-chevron {
    transition: transform 0.22s ease;
    flex-shrink: 0;
  }
  .hm2-cat-btn.open .hm2-cat-chevron { transform: rotate(180deg); }

  /* ── Divider between btn and nav links ── */
  .hm2-sep {
    width: 1px;
    height: 20px;
    background: #e2e8f0;
    margin: auto 10px auto 4px;
    flex-shrink: 0;
  }

  /* ── Inline nav links (Women / Men / Kids / …) ── */
  .hm2-navlink-wrap {
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .hm2-navlink {
    display: inline-flex;
    align-items: center;
    height: 44px;
    padding: 0 14px;
    font-family: 'Inter', sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    color: #475569;
    text-decoration: none;
    white-space: nowrap;
    border-bottom: 2px solid transparent;
    transition: color 0.18s, border-color 0.18s;
    cursor: pointer;
    background: none;
    border-top: none;
    border-left: none;
    border-right: none;
    border-radius: 0;
  }
  .hm2-navlink:hover,
  .hm2-navlink.open {
    color: #14b8a6;
    border-bottom-color: #14b8a6;
  }

  /* ── Mega menu dropdown shell ── */
  .hm2-drop-anchor {
    position: absolute;
    /* align left edge of dropdown with left edge of the bar, not the button */
    left: 0;
    top: calc(100% + 0px);
    z-index: 10000;
    /* Bridge gap so mouse can travel button → panel */
    padding-top: 4px;
    animation: hm2FadeIn 0.2s ease;
  }

  /* Make the mega menu span wide from the bar's left */
  .hm2-bar {
    position: relative; /* anchor for the full-width drop */
  }

  /* Full-width drop (attached to bar, not to btn/link) */
  .hm2-mega-anchor {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    z-index: 10000;
    padding-top: 0;
    animation: hm2FadeIn 0.2s ease;
  }

  .hm2-mega-inner {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 24px;
  }

  @media (max-width: 768px) {
    .hm2-bar { position: static; } /* Fix for full-width overlay if needed */
    .hm2-inner {
      height: 48px;
      padding: 0 12px;
    }
    .hm2-navlink {
      height: 48px;
      font-size: 13px;
      padding: 0 12px;
    }
    .hm2-cat-btn {
      height: 32px;
      padding: 0 10px;
      font-size: 13px;
    }
    .hm2-mega-anchor {
      position: fixed;
      left: 0;
      right: 0;
      top: 108px; /* Approx header height */
      bottom: 0;
      background: rgba(0,0,0,0.4); /* backdrop */
      padding-top: 0;
    }
    .hm2-mega-inner {
      padding: 0;
      height: 100%;
    }
  }
`;

function Hmain2() {
  const [openMenu, setOpenMenu] = useState(false); // 'cat' | 'link-{id}' | false
  const closeTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Load categories to display as nav links (Women, Men, Kids …)
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    if (!navigator.onLine) return;
    axios
      .get(`${base_url}category`)
      .then((r) =>
        setCategories(Array.isArray(r.data) ? r.data.slice(0, 6) : []),
      )
      .catch(() => {});
  }, []);

  const open = (id) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(id);
  };
  const startClose = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(false), 140);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);

  const isOpen = !!openMenu;

  return (
    <>
      <style>{STYLES}</style>

      {/* The bar itself is our positioning context */}
      <div className="hm2-bar" onMouseLeave={startClose}>
        <div className="hm2-inner">
          {/* ── "Categories ▾" button ── */}
          <div 
            className="hm2-cat-wrap" 
            onMouseEnter={() => open("cat")}
            onClick={() => {
              if (window.innerWidth <= 768) {
                setOpenMenu(openMenu === "cat" ? false : "cat");
              }
            }}
          >
            <button
              className={`hm2-cat-btn${openMenu === "cat" ? " open" : ""}`}
              aria-haspopup="true"
              aria-expanded={openMenu === "cat"}
              type="button"
            >
              Categories
              <svg
                className="hm2-cat-chevron"
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>

          <div className="hm2-navlink-wrap">
            <button
              className={`hm2-navlink${location.pathname === "/care-suggestions" ? " open" : ""}`}
              onClick={() => navigate("/care-suggestions")}
            >
              Health Advice
            </button>
          </div>

          <div className="hm2-navlink-wrap">
            <button
              className={`hm2-navlink${location.pathname === "/special-offer" ? " open" : ""}`}
              onClick={() => navigate("/special-offer")}
            >
              Special Offers
            </button>
          </div>

          {/* Thin separator */}
          <div className="hm2-sep" />

          {/* ── Inline category nav links (Women / Men / Kids …) ── */}
          {categories.map((cat) => {
            const id = cat._id || cat.title;
            return (
              <div
                key={id}
                className="hm2-navlink-wrap"
                onMouseEnter={() => open(id)}
              >
                <button
                  className={`hm2-navlink${openMenu === id ? " open" : ""}`}
                  onClick={() => {
                    const title = cat.title || cat.name;
                    const routes = {
                      "Medicines": "/medicines",
                      "OTC": "/otc",
                      "Vitamins": "/vitamins",
                      "Mother & Baby": "/mother-baby",
                      "Prescription": "/medicines"
                    };
                    if (routes[title]) {
                      navigate(routes[title]);
                    } else {
                      navigate(`/shop?category=${encodeURIComponent(title)}`);
                    }
                  }}
                  aria-haspopup="true"
                  aria-expanded={openMenu === id}
                >
                  {cat.title || cat.name}
                </button>
              </div>
            );
          })}
        </div>

        {/* ── MEGA MENU (full-width, anchored to bar) ── */}
        {isOpen && (
          <div
            className="hm2-mega-anchor"
            onMouseEnter={cancelClose}
            onMouseLeave={startClose}
          >
            <div
              style={{
                background: "#fff",
                borderTop: "1px solid #f0f0f0",
                boxShadow:
                  "0 16px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
              }}
            >
              <div className="hm2-mega-inner">
                {/* Pass the active category — if a link is hovered pass that cat id */}
                <HD activeCategoryId={openMenu === "cat" ? null : openMenu} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Hmain2;
